import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { HostListingPreviewCard } from '../components/host-onboarding/HostListingPreviewCard';
import { hostCardStyle } from '../components/HostSettingsCard';
import { storePendingHostListingInviteData, storePendingHostListingInviteId } from '../utils/deepLinkHandler';
import InviteAppGate from '../components/InviteAppGate';
import { useBackTarget } from '../hooks/useBackTarget';
import { trackInviteViewed } from '../utils/airbridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const infoCardStyle = {
  ...hostCardStyle,
  width: '100%',
  padding: '22px 18px',
  display: 'grid',
  gap: 14,
};

const actionButtonStyle = {
  width: '100%',
  borderRadius: 9999,
  padding: '14px 18px',
  fontFamily: "'Inria Serif', serif",
  fontSize: 24,
  cursor: 'pointer',
};

export default function HostReferralEntryPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const { inviteId } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadInvite = async () => {
      storePendingHostListingInviteId(inviteId);
      const searchParams = new URLSearchParams(location.search);
      const sharedListingUrl = searchParams.get('listing_url');
      if (sharedListingUrl) {
        const queryInvite = {
          invite_id: inviteId,
          listing_url: sharedListingUrl,
          listing_title: searchParams.get('title') || '',
          listing_image: searchParams.get('image') || '',
          listing_location_label: searchParams.get('location') || '',
        };
        setInvite(queryInvite);
        storePendingHostListingInviteData(queryInvite);
      }
      try {
        const response = await axios.get(`${API}/host/listing-invites/${inviteId}/landing`);
        setInvite(response.data);
        storePendingHostListingInviteData(response.data);
        if (inviteId) {
          trackInviteViewed({
            inviteId,
            inviteType: 'host_to_host',
            extra: { variant: response.data?.variant || 'a' },
          });
        }
      } catch (err) {
        if (!sharedListingUrl) {
          setError(err.response?.data?.detail || 'Invite link not found');
        }
        if (inviteId) {
          trackInviteViewed({ inviteId, inviteType: 'host_to_host', extra: { variant: 'unknown' } });
        }
      }
    };

    void loadInvite();
  }, [inviteId, location.search]);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();

  const primaryAction = currentUser
    ? currentUser.role === 'host'
      ? { label: 'Continue to host setup', onClick: () => navigate('/host-verify-listing', { replace: true }) }
      : { label: 'Continue as Airbnb Host', onClick: () => navigate('/switch-role', { replace: true }) }
    : { label: 'Sign In', onClick: () => navigate('/login', { replace: true }) };

  const isExpired = Boolean(invite?.expired);

  const subLabel = invite?.listing_location_label
    ? `Listing: ${invite.listing_title || 'Airbnb stay'} · ${invite.listing_location_label}`
    : invite?.listing_title || '';

  return (
    <InviteAppGate
      invitedToLabel={isExpired ? 'This invite has expired' : 'A host invited you to join Allure'}
      invitedSubLabel={
        isExpired
          ? 'You can still join Allure — install the app and set up as a host.'
          : `${subLabel ? `${subLabel} · ` : ''}Join through this invite and start with a higher commission.`
      }
      inviteId={inviteId}
      storePending={storePendingHostListingInviteId}
    >
      <div className="app-shell" data-testid="host-referral-entry-page" style={{ backgroundColor: 'var(--app-bg)', paddingLeft: 18, paddingRight: 18, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
        <button data-testid="host-referral-entry-back-button" onClick={goBack} style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 18px)', left: 18, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={34} color="var(--text)" strokeWidth={1.6} />
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 62px)' }}>
          <motion.img
            src="/allure-logo.png"
            alt="Allure"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ width: 158, height: 140, objectFit: 'contain' }}
          />

          <motion.h1
            data-testid="host-referral-entry-heading"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ fontFamily: "'Inria Serif', serif", fontSize: 34, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.04, margin: 0 }}
          >
            {error ? 'Invite unavailable' : isExpired ? 'This invite has expired' : 'A host invited you to join Allure'}
          </motion.h1>

          <motion.div
            data-testid="host-referral-entry-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, delay: 0.27, ease: [0.25, 0.1, 0.25, 1] }}
            style={infoCardStyle}
          >
            {error ? (
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, color: 'var(--text)', textAlign: 'center', margin: 0 }}>{error}</p>
            ) : isExpired ? (
              <>
                <p data-testid="host-referral-entry-expired-copy" style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, color: 'var(--text)', textAlign: 'center', lineHeight: 1.25, margin: 0 }}>
                  Personal invitations are valid for 48 hours, and this one has run out.
                </p>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text-2)', textAlign: 'center', lineHeight: 1.3, margin: 0 }}>
                  The good news: you can still join Allure without the invite — continue right here, or find Allure anytime on Google Play (or in Safari on iPhone). Set up takes minutes and joining is free.
                </p>
              </>
            ) : (
              <>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 21, color: 'var(--text)', textAlign: 'center', lineHeight: 1.15, margin: 0 }}>
                  I'm a host and I've started using Allure to elevate my guests' experience and earn more from every stay.
                </p>
                <div style={{ fontFamily: "'Inria Serif', serif", fontSize: 19, color: 'var(--text)', lineHeight: 1.35 }}>
                  <div>• Guests get exclusive discounts at top venues</div>
                  <div>• You earn a commission from their spending</div>
                  <div>• Monthly rewards + guests can tip you</div>
                  <div data-testid="host-referral-entry-boost-bullet">• Join through this invite and start with a higher commission</div>
                  <div>• Quick setup, no cost to join</div>
                </div>
              </>
            )}
          </motion.div>

          {!error ? (
            <HostListingPreviewCard
              preview={{
                title: invite?.listing_title || '',
                image: invite?.listing_image || '',
                location_label: invite?.listing_location_label || '',
              }}
            />
          ) : null}

          <motion.button
            data-testid="host-referral-entry-primary-button"
            onClick={primaryAction.onClick}
            disabled={!invite && !error}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.38, ease: [0.25, 0.1, 0.25, 1] }}
            style={{
              ...actionButtonStyle,
              border: 'none',
              background: 'var(--accent-purple)',
              color: '#FFFFFF',
              boxShadow: 'var(--premium-shadow-main)',
              opacity: !invite && !error ? 0.65 : 1,
              cursor: !invite && !error ? 'default' : 'pointer',
            }}
          >
            {!invite && !error ? 'Loading...' : primaryAction.label}
          </motion.button>

          {!currentUser && !error ? (
            <motion.button
              data-testid="host-referral-entry-register-button"
              onClick={() => navigate('/register', { replace: true })}
              disabled={!invite}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.48, ease: [0.25, 0.1, 0.25, 1] }}
              style={{
                ...actionButtonStyle,
                border: '1px solid var(--card-border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                opacity: !invite ? 0.65 : 1,
                cursor: !invite ? 'default' : 'pointer',
              }}
            >
              Create account
            </motion.button>
          ) : null}
        </div>
      </div>
    </InviteAppGate>
  );
}
